/**
 * Preview Panel Header - displays item name, icon, and actions
 */

import { Star, Maximize2, Minimize2, X } from 'lucide-react';
import { Button, cn } from '@loader/ui';
import type { WorkspaceItemType, ConnectorType } from '@loader/shared-types';

import { ItemIcon } from './shared';
import { useSidePanelStore } from '../../stores';
import { ConnectorLabel } from '../ConnectorLabel';

interface PreviewPanelHeaderProps {
  name: string;
  type: WorkspaceItemType;
  description?: string | null;
  connectorType?: ConnectorType | null;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onClose: () => void;
}

export function PreviewPanelHeader({
  name,
  type,
  description,
  connectorType,
  isFavorite,
  onToggleFavorite,
  onClose,
}: PreviewPanelHeaderProps) {
  const { isExpanded, expand, collapse } = useSidePanelStore();

  const handleToggleExpand = () => {
    if (isExpanded) {
      collapse();
    } else {
      expand();
    }
  };

  return (
    <div className="flex items-center justify-between border-b px-4 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <ItemIcon type={type} className={cn('shrink-0', description ? 'size-9' : 'size-5')} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-semibold truncate">{name}</span>
            {connectorType && (
              <ConnectorLabel connectorType={connectorType} />
            )}
          </div>
          {description && <p className="text-muted-foreground truncate text-sm">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {type !== 'folder' && (
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={onToggleFavorite}
          >
            <Star className={cn('size-4', isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground')} />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={handleToggleExpand}
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <Minimize2 className="size-4 text-muted-foreground" />
          ) : (
            <Maximize2 className="size-4 text-muted-foreground" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={onClose}
          title="Close"
        >
          <X className="size-4 text-muted-foreground" />
        </Button>
      </div>
    </div>
  );
}
